/** @jsxImportSource react */
import React, { useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast';
import { useTranslation } from '../i18n'
import { useAuth } from '../contexts/auth'
import { UserRole } from '../types'
import { api } from '../services/api'
import {
  Calendar,
  BarChart2 as BarChartIcon,
  Download,
  FileText,
  Clock,
  TrendingUp,
  AlertCircle,
  Info,
  Users,
  Ship,
  DollarSign,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
const ReportsPage = () => {
  const { t, formatDate, formatCurrency } = useTranslation()
  const { user } = useAuth()
  
  const getTodayString = () => new Date().toISOString().split('T')[0];
  
  const [startDate, setStartDate] = useState(getTodayString())
  const [endDate, setEndDate] = useState(getTodayString())

  const [activeTab, setActiveTab] = useState('orders')
  const { data: orders = [], isLoading: isLoadingOrders } = useQuery({
    queryKey: ['allOrdersForReport'],
    queryFn: () => api.getOrders(),
    enabled: !!user && user.role === UserRole.Admin,
  })
  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['allUsersForReport'],
    queryFn: () => api.getUsers(),
    enabled: !!user && user.role === UserRole.Admin,
  })
  const filteredOrders = useMemo(() => {
    if (!startDate || !endDate) return []
    return orders.filter((order) => {
      const orderDate = new Date(order.createdAt)
      const endOfDay = new Date(endDate)
      endOfDay.setHours(23, 59, 59, 999)
      return orderDate >= new Date(startDate) && orderDate <= endOfDay
    })
  }, [orders, startDate, endDate])

  const handleDatePreset = (preset: 'today' | 'week' | 'month' | 'year') => {
      const today = new Date();
      let start = new Date();
      const end = new Date();

      switch (preset) {
          case 'today':
              start = today;
              break;
          case 'week':
              start.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Week starts on Monday
              break;
          case 'month':
              start = new Date(today.getFullYear(), today.getMonth(), 1);
              break;
          case 'year':
              start = new Date(today.getFullYear(), 0, 1);
              break;
      }

      const formatDateForInput = (date: Date) => date.toISOString().split('T')[0];
      
      setStartDate(formatDateForInput(start));
      setEndDate(formatDateForInput(end));
  };


  const handleDownloadCSV = () => {
    if (!startDate || !endDate) {
      toast.error(t('selectDateRangeError'))
      return
    }
    const clientNameMap = new Map(users.map((u) => [u.id, u.name]))
    if (filteredOrders.length === 0) {
      toast.error(
        t('noOrdersInRangeError')
      )
      return
    }
    const headers = [
      t('orderId'),
      t('product'),
      t('client'),
      t('status'),
      t('paymentStatus'),
      t('orderType'),
      t('date'),
      t('finalPrice'),
      t('commission'),
      t('shippingType'),
      t('carrier'),
      t('trackingNumber'),
    ]
    const formatValue = (value: any) => {
      if (value === null || value === undefined) return ''
      const strValue = String(value)
      if (/[",\n]/.test(strValue)) {
        return `"${strValue.replace(/"/g, '""')}"`
      }
      return strValue
    }
    const csvContent = [
      headers.join(','),
      ...filteredOrders.map((order) =>
        [
          formatValue(order.orderId),
          formatValue(order.productName),
          formatValue(clientNameMap.get(order.clientId) || order.clientId),
          formatValue(order.status),
          formatValue(order.paymentStatus),
          formatValue(order.orderType),
          formatValue(new Date(order.createdAt).toISOString()),
          formatValue(order.finalPrice),
          formatValue(order.commission),
          formatValue(order.shippingType),
          formatValue(order.carrier),
          formatValue(order.trackingNumber),
        ].join(','),
      ),
    ].join('\n')
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `orders-report-${startDate}-to-${endDate}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
    toast.success(
      t('reportDownloadedSuccess')
    )
  }
  
  const monthlyData = useMemo(() => {
    const months: { [key: string]: { name: string; count: number; revenue: number } } = {}
    filteredOrders.forEach((order) => {
      const date = new Date(order.createdAt)
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!months[monthYear]) {
        months[monthYear] = {
          name: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), { month: 'short', year: 'numeric' }),
          count: 0,
          revenue: 0,
        }
      }
      months[monthYear].count++
      if (order.finalPrice) {
        months[monthYear].revenue += order.finalPrice
      }
    })
    return Object.values(months).sort((a, b) => {
      const dateA = new Date(a.name)
      const dateB = new Date(b.name)
      return dateA.getTime() - dateB.getTime()
    })
  }, [filteredOrders, formatDate])
  

  if (user?.role !== UserRole.Admin) {
    return <Navigate to="/dashboard" />
  }
  const isLoading = isLoadingOrders || isLoadingUsers
  const totalOrders = filteredOrders.length
  const totalRevenue = filteredOrders.reduce(
    (sum, order) => sum + (order.finalPrice || 0),
    0,
  )
  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {t('reports')}
          </h1>
          <p className="text-slate-600 max-w-3xl">
            {t('reportsDescription')}
          </p>
        </div>
        {/* Tab Navigation */}
        <div className="mb-6 border-b border-slate-200">
          <nav
            className="flex space-x-6 overflow-x-auto pb-2"
            aria-label="Report tabs"
          >
            <button
              onClick={() => setActiveTab('orders')}
              className={`flex items-center pb-2 px-1 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'orders' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'}`}
            >
              <FileText className="w-4 h-4 me-2" />
              {t('ordersReport')}
            </button>
            <button
              onClick={() => setActiveTab('revenue')}
              className={`flex items-center pb-2 px-1 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'revenue' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'}`}
            >
              <TrendingUp className="w-4 h-4 me-2" />
              {t('revenueAnalysis')}
            </button>
            <button
              onClick={() => setActiveTab('future')}
              className={`flex items-center pb-2 px-1 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${activeTab === 'future' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'}`}
            >
              <Clock className="w-4 h-4 me-2" />
              {t('futureReports')}
            </button>
          </nav>
        </div>
        {/* Date Range Selector */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary-500" />
            <h2 className="text-lg font-semibold text-slate-900">
              {t('selectDateRange')}
            </h2>
          </div>
           <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={() => handleDatePreset('today')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">{t('today')}</button>
                <button onClick={() => handleDatePreset('week')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">{t('thisWeek')}</button>
                <button onClick={() => handleDatePreset('month')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">{t('thisMonth')}</button>
                <button onClick={() => handleDatePreset('year')} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">{t('thisYear')}</button>
            </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label
                htmlFor="start-date"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                {t('startDate')}
              </label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label={t('startDate')}
              />
            </div>
            <div>
              <label
                htmlFor="end-date"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                {t('endDate')}
              </label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label={t('endDate')}
              />
            </div>
            <div>
              <button
                onClick={handleDownloadCSV}
                disabled={isLoading || !startDate || !endDate}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg font-medium hover:from-primary-700 hover:to-primary-600 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm"
              >
                <Download className="w-4 h-4" />
                {isLoading
                  ? t('loadingData')
                  : t('downloadCsv')}
              </button>
            </div>
          </div>
          {filteredOrders.length > 0 && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm text-slate-500">
                  {t('totalOrders')}
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {totalOrders}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm text-slate-500">
                  {t('totalRevenue')}
                </p>
                <p className="text-2xl font-bold text-primary-600">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <p className="text-sm text-slate-500">
                  {t('dateRange')}
                </p>
                <p className="text-lg font-medium text-slate-900">
                  {formatDate(startDate)} - {formatDate(endDate)}
                </p>
              </div>
            </div>
          )}
          {!startDate || !endDate ? (
            <div className="mt-4 flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">
                {t('selectDateRangePrompt')}
              </p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="mt-4 flex items-center gap-2 text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Info className="w-5 h-5" />
              <p className="text-sm">
                {t('noOrdersInRange')}
              </p>
            </div>
          ) : null}
        </div>
        {/* Reports Content */}
        {activeTab === 'orders' && filteredOrders.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Monthly Order Trends */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <BarChartIcon className="w-5 h-5 text-primary-500" />
                  <h3 className="font-semibold text-slate-900">
                    {t('monthlyOrderTrends')}
                  </h3>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f0f0f0"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{
                        fontSize: 12,
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fontSize: 12,
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#ED5F1A"
                      name={t('orders')}
                      radius={[4, 4, 0, 0]}
                      barSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'revenue' && filteredOrders.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary-500" />
                <h3 className="font-semibold text-slate-900">
                  {t('revenueByMonth')}
                </h3>
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f0f0"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) => `$${Number(value) / 1000}k`}
                  />
                  <Tooltip
                    formatter={(value: number) => [
                      formatCurrency(value),
                      t('revenue'),
                    ]}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#203946"
                    name={t('revenue')}
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {activeTab === 'future' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mb-6 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {t('moreReportsComingSoon')}
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto mb-6">
              {t('moreReportsDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full mx-auto flex items-center justify-center mb-3">
                  <Users className="w-5 h-5" />
                </div>
                <h4 className="font-medium text-slate-800 mb-1">
                  {t('clientAnalytics')}
                </h4>
                <p className="text-sm text-slate-600">
                  {t('clientAnalyticsDesc')}
                </p>
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full mx-auto flex items-center justify-center mb-3">
                  <Ship className="w-5 h-5" />
                </div>
                <h4 className="font-medium text-slate-800 mb-1">
                  {t('shippingMetrics')}
                </h4>
                <p className="text-sm text-slate-600">
                  {t('shippingMetricsDesc')}
                </p>
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-full mx-auto flex items-center justify-center mb-3">
                  <DollarSign className="w-5 h-5" />
                </div>
                <h4 className="font-medium text-slate-800 mb-1">
                  {t('financialForecasts')}
                </h4>
                <p className="text-sm text-slate-600">
                  {t('financialForecastsDesc')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default ReportsPage